import { useState, useCallback, useEffect, useRef } from "react";
import { api } from "../../../lib/api";
import { logger } from "../../../lib/terminalLogger";

const API_PATH = "/users/me/tool-favorites";
const LOCAL_STORAGE_KEY = "cdr:tools:favorites";

/**
 * Hook para gerenciar ferramentas favoritas do usuario.
 * Persiste no backend e usa localStorage como cache local.
 */
export function useToolFavorites() {
	const [favorites, setFavorites] = useState<string[]>(() => {
		try {
			const saved = window.localStorage.getItem(LOCAL_STORAGE_KEY);
			return saved ? JSON.parse(saved) : [];
		} catch {
			return [];
		}
	});
	const [isLoading, setIsLoading] = useState(false);
	const loadedRef = useRef(false);

	// Carregar favoritos da API ao montar
	useEffect(() => {
		if (loadedRef.current) return;
		loadedRef.current = true;

		(async () => {
			try {
				const response = await api(API_PATH, { method: "GET" });
				if (response?.success && Array.isArray(response.data)) {
					setFavorites(response.data);
					try {
						window.localStorage.setItem(
							LOCAL_STORAGE_KEY,
							JSON.stringify(response.data),
						);
					} catch {}
				}
			} catch (err: any) {
				logger.warn(
					"[ToolFavorites] Falha ao carregar favoritos:",
					err?.message,
				);
			}
		})();
	}, []);

	// Toggle favorito (add/remove)
	const toggleFavorite = useCallback(async (toolId: string) => {
		setFavorites((prev) => {
			const next = prev.includes(toolId)
				? prev.filter((id) => id !== toolId)
				: [...prev, toolId];

			// Salvar localmente imediato
			try {
				window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
			} catch {}

			// Salvar no backend (fire-and-forget)
			(async () => {
				try {
					await api(API_PATH, {
						method: "PUT",
						body: JSON.stringify({ favorites: next }),
					});
				} catch (err: any) {
					logger.warn(
						"[ToolFavorites] Falha ao salvar favoritos:",
						err?.message,
					);
				}
			})();

			return next;
		});
	}, []);

	const isFavorite = useCallback(
		(toolId: string) => {
			return favorites.includes(toolId);
		},
		[favorites],
	);

	return { favorites, toggleFavorite, isFavorite, isLoading };
}
